import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { lettersApi, ApiError } from "@/api";
import type {
  LetterDuplicatesResponse,
  LetterDuplicateGroup,
  LetterDuplicateMember,
  LetterMergePreview,
  LetterSource,
} from "@/types";
import { Button, Pill, Spinner, Toast, useToast } from "@/components/ui";
import type { StatusTone } from "@/lib/status";
import { humanise } from "@/lib/jobs";
import { formatDateDMY, formatDateTime } from "@/lib/format";
import { useUnread } from "@/staff/letters/UnreadContext";
import "./review.css";
import "./reviewLetters.css";

/* ─── display helpers ──────────────────────────────────────────────────── */

function sourceLabel(s: LetterSource): string {
  return s === "building_control" ? "Building control" : "Planning";
}

function confidenceTone(c: string | null): StatusTone {
  if (c === "high") return "success";
  if (c === "low") return "accent";
  return "info-soft";
}

/** Letter type · subtype, humanised, dropping blanks. */
function letterKindLabel(m: LetterDuplicateMember): string {
  return [m.letter_type, m.letter_subtype]
    .map((s) => (s ? humanise(s) : ""))
    .filter(Boolean)
    .join(" · ") || "—";
}

function titleOf(m: LetterDuplicateMember): string {
  return m.source_document_title || `letter ${m.id}`;
}

type SourceFilter = "all" | LetterSource;

/* ─── screen ───────────────────────────────────────────────────────────── */

/** Review › Letters — surfaces letters that look like the same document parsed
 *  twice (exact = identical title; possible = same app/date/type, title differs)
 *  and soft-archives the duplicates into one keeper via a dry-run-gated merge. */
export function ReviewLetters() {
  const [data, setData] = useState<LetterDuplicatesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);
  const [source, setSource] = useState<SourceFilter>("all");
  const [mergeTarget, setMergeTarget] = useState<LetterDuplicateGroup | null>(null);
  const { toast, showToast } = useToast();
  // Archiving unread duplicates changes the unread-letters count — refresh the
  // nav badge immediately rather than waiting for its 60s poll.
  const { refresh: refreshUnread } = useUnread();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    lettersApi
      .getLetterDuplicates(source === "all" ? undefined : source)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load the duplicate-letters queue.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey, source]);

  const counts = data?.counts;
  const exact = data?.duplicate_letters ?? [];
  const possible = data?.possible_duplicate_letters ?? [];

  return (
    <section className="review">
      <header className="review__head">
        <div>
          <h1>Duplicate letters</h1>
          {counts ? (
            <p className="review__count">
              {counts.duplicate_groups} exact · {counts.possible_duplicate_groups} possible
            </p>
          ) : null}
          <p className="review__intro">
            Letters that look like the same document parsed twice — grouped by application, date and
            type. Open each copy's PDF to confirm, then keep one and archive the rest.
          </p>
        </div>
        <div className="review-letters__controls">
          <div className="review-letters__tabs" role="tablist" aria-label="Source">
            {(["all", "planning", "building_control"] as const).map((s) => (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={source === s}
                className={`review-letters__tab${source === s ? " is-active" : ""}`}
                onClick={() => setSource(s)}
              >
                {s === "all" ? "All" : sourceLabel(s)}
              </button>
            ))}
          </div>
          <Button variant="ghost" size="sm" onClick={() => setReloadKey((k) => k + 1)} disabled={loading}>
            Refresh
          </Button>
        </div>
      </header>

      {error ? (
        <p className="leads__error review__error" role="alert">
          {error}
          <button type="button" className="review__retry" onClick={() => setReloadKey((k) => k + 1)}>
            Retry
          </button>
        </p>
      ) : null}

      {loading ? (
        <div className="review__loading">
          <Spinner label="Loading duplicate letters…" />
        </div>
      ) : data ? (
        exact.length === 0 && possible.length === 0 ? (
          <p className="review__empty">No duplicate letters found — every letter looks unique.</p>
        ) : (
          <>
            {exact.length > 0 ? (
              <div className="review__section">
                <h2 className="review__section-title">Exact duplicates ({exact.length})</h2>
                <p className="review__section-note">
                  Same application, date and type with an identical document title — almost certainly
                  the same letter ingested twice.
                </p>
                {exact.map((group) => (
                  <DupeGroupCard key={`ex-${group.source}-${group.letters[0]?.id}`} group={group} onResolve={() => setMergeTarget(group)} />
                ))}
              </div>
            ) : null}

            {possible.length > 0 ? (
              <div className="review__section">
                <h2 className="review__section-title">Possible duplicates ({possible.length})</h2>
                <p className="review__section-note">
                  Same application, date and type but the document titles differ — could be one letter
                  re-titled, or two genuinely different letters. Check the PDFs before merging.
                </p>
                {possible.map((group) => (
                  <DupeGroupCard key={`po-${group.source}-${group.letters[0]?.id}`} group={group} onResolve={() => setMergeTarget(group)} />
                ))}
              </div>
            ) : null}
          </>
        )
      ) : null}

      {mergeTarget ? (
        <MergeDialog
          group={mergeTarget}
          onClose={() => setMergeTarget(null)}
          onMerged={(message) => {
            setMergeTarget(null);
            showToast(message);
            setReloadKey((k) => k + 1);
            void refreshUnread();
          }}
        />
      ) : null}

      <Toast toast={toast} />
    </section>
  );
}

/* ─── duplicate group card ─────────────────────────────────────────────── */

function DupeGroupCard({ group, onResolve }: { group: LetterDuplicateGroup; onResolve: () => void }) {
  const jobId = group.letters.find((l) => l.job_id > 0)?.job_id ?? 0;
  const jobName = group.letters.find((l) => l.job_name)?.job_name ?? null;
  return (
    <article className="review-dupe">
      <header className="review-dupe__head">
        <span className="review-dupe__key">{group.group_key}</span>
        <Pill tone="neutral">{sourceLabel(group.source)}</Pill>
        <Pill tone={group.exact ? "success" : "info-soft"}>{group.exact ? "Exact" : "Possible"}</Pill>
        <span className="review-dupe__count">{group.count} copies</span>
        {jobId > 0 ? (
          <Link className="review-letters__joblink" to={`/staff/jobs/${jobId}`}>
            {jobName ? `${jobName} ↗` : `Job #${jobId} ↗`}
          </Link>
        ) : (
          <span className="review-dupe__unlinked">Unlinked</span>
        )}
        <Button
          size="sm"
          variant="secondary"
          className="review-dupe__merge"
          onClick={onResolve}
          title={`Keep one of these ${group.count} letters and archive the rest`}
        >
          Resolve
        </Button>
      </header>
      <ul className="review-letters__members">
        {group.letters.map((m) => (
          <li key={m.id} className="review-letters__member">
            <div className="review-letters__member-main">
              <span className="review-letters__member-title">{titleOf(m)}</span>
              <span className="review-letters__member-meta">
                {letterKindLabel(m)} · {formatDateDMY(m.letter_date)}
                {m.addressed_to ? ` · to ${m.addressed_to}` : ""}
              </span>
            </div>
            <div className="review-letters__member-side">
              {m.confidence ? <Pill tone={confidenceTone(m.confidence)}>{m.confidence}</Pill> : null}
              {m.task_id > 0 ? <Pill tone="info-soft">task linked</Pill> : null}
              <span className="review-letters__ingested" title="When this copy was ingested">
                #{m.id} · {formatDateTime(m.created_at)}
              </span>
              {m.pdf_url ? (
                <a className="review-letters__pdf" href={m.pdf_url} target="_blank" rel="noreferrer">
                  PDF ↗
                </a>
              ) : (
                <span className="review-letters__pdf review-letters__pdf--none">no PDF</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </article>
  );
}

/* ─── merge preview + confirm dialog ───────────────────────────────────── */

function MergeDialog({
  group,
  onClose,
  onMerged,
}: {
  group: LetterDuplicateGroup;
  onClose: () => void;
  onMerged: (message: string) => void;
}) {
  const ids = useMemo(() => group.letters.map((l) => l.id), [group]);

  const [preview, setPreview] = useState<LetterMergePreview | null>(null);
  const [keeperId, setKeeperId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [committing, setCommitting] = useState(false);

  // Initial dry-run picks the default keeper (most recent). ids/source are fixed
  // for a given dialog instance.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    lettersApi
      .previewLetterMerge(ids, group.source)
      .then((p) => {
        if (cancelled) return;
        setPreview(p);
        setKeeperId(p.keeper_id);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not preview the merge.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ids, group.source]);

  // Esc closes (unless a commit is in flight).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !committing) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [committing, onClose]);

  // Picking a different keeper re-previews so the effects (losers, receipts,
  // inherited task) stay accurate.
  function chooseKeeper(id: number) {
    if (id === keeperId || committing) return;
    setKeeperId(id);
    setLoading(true);
    setError(undefined);
    lettersApi
      .previewLetterMerge(ids, group.source, id)
      .then(setPreview)
      .catch((err) => setError(err instanceof ApiError ? err.message : "Could not preview the merge."))
      .finally(() => setLoading(false));
  }

  async function confirm() {
    if (keeperId == null) return;
    setCommitting(true);
    setError(undefined);
    try {
      const res = await lettersApi.mergeLetters(ids, group.source, keeperId);
      const n = res.losers_merged.length;
      onMerged(`Archived ${n} duplicate letter${n === 1 ? "" : "s"} into #${res.keeper_id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not merge the letters.");
      setCommitting(false);
    }
  }

  const losers = preview?.losers_to_merge ?? [];

  return (
    <div className="review-modal__overlay" role="dialog" aria-modal="true" onClick={committing ? undefined : onClose}>
      <div className="review-modal" onClick={(e) => e.stopPropagation()}>
        <div className="review-modal__head">
          <h2>Resolve duplicate letters</h2>
          <span className="review-modal__key">{group.group_key}</span>
        </div>

        <div className="review-modal__body">
          <p className="review-letters__pick-label">Keep this copy:</p>
          <ul className="review-letters__pick">
            {group.letters.map((m) => (
              <li key={m.id}>
                <label className={`review-letters__pick-row${keeperId === m.id ? " is-keeper" : ""}`}>
                  <input
                    type="radio"
                    name="keeper"
                    checked={keeperId === m.id}
                    disabled={committing}
                    onChange={() => chooseKeeper(m.id)}
                  />
                  <span className="review-letters__pick-main">
                    <span className="review-letters__pick-title">{titleOf(m)}</span>
                    <span className="review-letters__pick-meta">
                      #{m.id} · ingested {formatDateTime(m.created_at)}
                      {m.task_id > 0 ? " · task linked" : ""}
                    </span>
                  </span>
                  {m.pdf_url ? (
                    <a
                      className="review-letters__pdf"
                      href={m.pdf_url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      PDF ↗
                    </a>
                  ) : null}
                </label>
              </li>
            ))}
          </ul>

          {loading ? (
            <Spinner label="Previewing…" />
          ) : preview ? (
            <ul className="review-modal__effects">
              <li>
                Archives <strong>{losers.length}</strong> letter{losers.length === 1 ? "" : "s"} (
                {losers.map((id) => `#${id}`).join(", ")}) — hidden from the inbox, not deleted.
              </li>
              {preview.read_receipts_to_repoint > 0 ? (
                <li>Moves {preview.read_receipts_to_repoint} read-receipt{preview.read_receipts_to_repoint === 1 ? "" : "s"} onto the kept letter.</li>
              ) : null}
              {preview.task_inherited_id > 0 ? (
                <li>The kept letter inherits the task link from #{preview.task_inherited_from_letter_id}.</li>
              ) : null}
            </ul>
          ) : null}

          {error ? <p className="leads__error" role="alert">{error}</p> : null}
        </div>

        <div className="review-modal__foot">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={committing}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={confirm}
            loading={committing}
            disabled={loading || !preview || keeperId == null}
          >
            {preview ? `Archive ${losers.length} duplicate${losers.length === 1 ? "" : "s"}` : "Merge"}
          </Button>
        </div>
      </div>
    </div>
  );
}
